import { useContext, useEffect, useMemo, useState } from "react";
import CompanyService from "../../services/CompanyService";
import UserService, { UserRole } from "../../services/UserService";
import {
  cashappPermissionGroups,
  cashappPermissionKeys
} from "../shared/cashappPermissions";
import SwitchButton from "../shared/components/SwitchButton";
import Tabs from "../shared/components/Tabs";
import ToastContext from "../shared/bootstrap/Toast";
import Dropdown from "../shared/components/Dropdown";
import {
  CheckBoxEditor,
  MultiselectEditor,
  TextEditor
} from "../shared/components/Editors";
import FloatingPanelService from "../shared/FloatingPanel";
import { TranslationService } from "../../services/TranslationService";
import { UserGetResponse } from "./entities/UserGetResponse";
import { RequiredManager, ValidationMessage } from "../shared/RequieredManager";
import { parseIntOrDefault, validateMail } from "../../utils/ParseUtils";
import { FloatingPanelFooter } from "../shared/components/FloatingPanelFooter";
import { getError } from "../../utils/RequestUtils";
import { DefaultModalProps } from "../shared/bootstrap/DefaultModal";
import ModalService from "../shared/bootstrap/modal/Modal";
import TooltipComponent from "../shared/TooltipComponent";
import { UserListGetResponse } from "./entities/UserListGetResponse";
import Select from "react-select";
import UserLimit from "./UserLimit";

const permissions = [
  "NewPerson",
  "EditPerson",
  "DeletePerson",
  "SetFieldsPerson",

  "NewIO",
  "EditIO",
  "DeleteIO",
  "SetFieldsIO",

  "EditIOStatus",
  "DisableIOStatusEdit",

  "NewTransaction",
  "EditTransaction",
  "DeleteTransaction",
  "SetFieldsTransaction",

  "EditTransactionType",

  "NewComment",
  "EditComment",
  "DeleteComment",
  "ImportComment",

  "NewWhatsapp",
  "EditWhatsapp",
  "DeleteWhatsapp",
  "SetTemplateWhatsapp",

  "NewMail",
  "EditMail",
  "DeleteMail",
  "SetTemplateEmail",

  "SetTemplateSMS",
  "SetPayment",

  "NewTask",
  "EditTask",
  "DeleteTask",
  "CheckTask",
  "CheckOthersTask",

  "NewTag",
  "EditTag",
  "DeleteTag",

  "ViewReport",

  "EditSegment",

  "ViewAction",
  "EditAction",

  "SetCompany",
  "SetCompanyOtherSettings",

  "ViewRole",
  "EditRole",

  "ViewUser",
  "EditUser",

  "EditActivityType",

  "ViewImportLog",
  "SetImport",

  "EditDashboard",

  "SetAIAssistant",

  "Export",
  "UseMCP"
];

export class UserEditModel {
  UserID: number = 0;
  email?: string;
  role?: UserRole = UserRole.Guest;
  segments: string[] = [];
  notificationSegments: string[] = [];
  creatorUserId?: number;
  chooseSegments: boolean = false;
  chooseNotificationSegments: boolean = false;
  DashboardPanelID?: number;
  CashApp: boolean = false;
  // Perfil cashapp (admin/invitado/consulta) independiente del de cobranzas.
  cashAppRole?: UserRole = UserRole.Guest;
  // Permisos cashapp efectivos (union de los roles asignados). Solo visual.
  cashAppPermissions: { [key: string]: boolean } = {};

  NewPerson: boolean = true;
  EditPerson: boolean = true;
  DeletePerson: boolean = true;
  SetFieldsPerson: boolean = true;

  NewIO: boolean = true;
  EditIO: boolean = true;
  DeleteIO: boolean = true;
  SetFieldsIO: boolean = true;

  EditIOStatus: boolean = true;
  DisableIOStatusEdit: boolean = false;

  NewTransaction: boolean = true;
  EditTransaction: boolean = true;
  DeleteTransaction: boolean = true;
  SetFieldsTransaction: boolean = true;

  EditTransactionType: boolean = true;

  NewComment: boolean = true;
  EditComment: boolean = true;
  DeleteComment: boolean = true;
  ImportComment: boolean = true;

  NewWhatsapp: boolean = true;
  EditWhatsapp: boolean = true;
  DeleteWhatsapp: boolean = true;
  SetTemplateWhatsapp: boolean = true;

  NewMail: boolean = true;
  EditMail: boolean = true;
  DeleteMail: boolean = true;
  SetTemplateEmail: boolean = true;

  SetTemplateSMS: boolean = true;
  SetPayment: boolean = true;

  NewTask: boolean = true;
  EditTask: boolean = true;
  DeleteTask: boolean = true;
  CheckTask: boolean = true;
  CheckOthersTask: boolean = true;

  NewTag: boolean = true;
  EditTag: boolean = true;
  DeleteTag: boolean = true;

  ViewReport: boolean = true;

  EditSegment: boolean = true;

  ViewAction: boolean = true;
  EditAction: boolean = true;

  SetCompany: boolean = true;
  SetCompanyOtherSettings: boolean = true;

  ViewRole: boolean = true;
  EditRole: boolean = true;

  ViewUser: boolean = true;
  EditUser: boolean = true;

  EditActivityType: boolean = true;

  ViewImportLog: boolean = true;
  SetImport: boolean = true;

  EditDashboard: boolean = true;

  SetAIAssistant: boolean = true;
  UseMCP: boolean = true;

  Export: boolean = true;

  roles: number[] = [];

  constructor(user?: UserGetResponse.User) {
    if (!user) {
      return;
    }

    this.UserID = user.UserID;
    this.email = user.email;
    this.DashboardPanelID = parseIntOrDefault(user.DashboardPanelID, undefined);
    this.CashApp = user.CashApp ?? false;
    this.segments = user.segments.map((x) => x.toString()) ?? [];
    this.notificationSegments =
      user.notificationSegments.map((x) => x.toString()) ?? [];
    this.chooseSegments = this.segments.length > 0;
    this.chooseNotificationSegments = this.notificationSegments.length > 0;
    this.creatorUserId = user.CreatorUserID;
    this.roles = user?.roles || [];
    if (user.Admin) {
      this.role = UserRole.Admin;
    } else if (user.ReadOnly) {
      this.role = UserRole.QueriesOnly;
    } else {
      this.role = UserRole.Guest;
    }

    if (user.AdminCashApp) {
      this.cashAppRole = UserRole.Admin;
    } else if (user.ReadOnlyCashApp) {
      this.cashAppRole = UserRole.QueriesOnly;
    } else {
      this.cashAppRole = UserRole.Guest;
    }

    // Permisos cashapp efectivos (los calcula el backend como union de los roles).
    cashappPermissionKeys.forEach((permission: string) => {
      this.cashAppPermissions[permission] = ((user as Record<string, unknown>)[
        permission
      ] ?? false) as boolean;
    });

    permissions.forEach((permission: string) => {
      (this[permission as keyof UserEditModel] as boolean) = (user[
        permission as keyof typeof user
      ] ?? false) as boolean;
    });
  }
}

const INVERTED_ON_CHECK_ALL = [
  "DisableIOStatusEdit",
  "CheckOthersTask"
] as const;

export const UserEdit = ({
  userId,
  panelOptions,
  onSubmit,
  readonly = false,
  roles = [],
  userByList
}: {
  userId?: number;
  readonly?: boolean;
  panelOptions: { value: number; text: string }[];
  onSubmit?: () => void;
  roles: any[];
  userByList?: UserListGetResponse.User;
}) => {
  const { showToast } = useContext(ToastContext);
  const { translate } = TranslationService;
  const { showDefaultModal } = ModalService;
  const [userEdit, setUserEdit] = useState(new UserEditModel());
  const [saving, setSaving] = useState(false);
  const [hasPassword, setHasPassword] = useState(true);
  const [activeTab, setActiveTab] = useState<"cobranzas" | "cashapp">(
    "cobranzas"
  );
  const segmentOptionList = CompanyService.getSegments();
  const requiredManager = new RequiredManager();
  const [lockedPermissions, setLockedPermissions] = useState<string[]>([]);

  // Calcular los segmentos seleccionados para el MultiselectEditor
  const selectedSegmentsValue = useMemo(() => {
    return userEdit.segments
      .map((x) => {
        const segment = segmentOptionList.find((y) => String(y.Id) === x);
        return segment
          ? { value: String(segment.Id), label: segment.Value }
          : null;
      })
      .filter(Boolean) as { value: string; label: string }[];
  }, [userEdit.segments, segmentOptionList]);

  const selectedNotificationSegmentsValue = useMemo(() => {
    return userEdit.notificationSegments
      .map((x) => {
        const segment = segmentOptionList.find((y) => String(y.Id) === x);
        return segment
          ? { value: String(segment.Id), label: segment.Value }
          : null;
      })
      .filter(Boolean) as { value: string; label: string }[];
  }, [userEdit.notificationSegments, segmentOptionList]);

  // The account owner comes from the company token; a null CreatorUserID only
  // means the user has no recorded creator (e.g. imported users).
  const isAccountOwner =
    userEdit.UserID > 0 &&
    userEdit.UserID.toString() === CompanyService.getCompanyToken()?.ownerId;
  const editPanel =
    userId !== undefined &&
    userEdit.UserID > 0 &&
    (isAccountOwner ||
      userEdit.UserID.toString() === CompanyService.getUserid());
  const isLoadingUser = () => userId && userEdit.UserID === 0;
  const userIsCreator = () => isAccountOwner;
  const canOnlyEditPanel = () =>
    userId &&
    (userIsCreator() || userId === parseInt(CompanyService.getUserid()));
  // Opcion 2 (bootstrap cashapp): en la vista restringida "solo panel" (editarte a vos mismo
  // o al Creador), solo el Creador editandose a si mismo puede tocar su perfil cashapp. Un admin
  // comun NO se auto-asigna cashapp (se lo asigna otro admin o el Creador).
  const editingSelf =
    userEdit.UserID > 0 &&
    userEdit.UserID.toString() === CompanyService.getUserid();
  const ownerSelfEdit = isAccountOwner && editingSelf;

  const updateUser =
    (propName: string) =>
    (value: string | string[] | number | boolean | undefined) => {
      setUserEdit((prevUserEdit) => ({ ...prevUserEdit, [propName]: value }));
    };
  const onEmailChange = requiredManager.makeRequired(updateUser("email"));
  const rolesSelected = roles
    ?.filter((r) => userEdit.roles?.includes(r.RoleID))
    ?.map((x) => ({ label: x.Name, value: x.RoleID }));

  useEffect(() => {
    const loadUser = async () => {
      if (!userId) {
        let newUser = new UserEditModel();
        setUserEdit(newUser);
        return;
      }
      const result = await UserService.get(userId.toString());
      if (result instanceof Error) {
        showToast(translate.ErrorProcessingRequest, undefined, "danger");
        return undefined;
      }
      const newUserEdit = new UserEditModel({
        ...result?.item,
        roles: userByList?.roles
      });
      console.log(newUserEdit);
      setHasPassword(result?.item.HasPassword ?? true);
      setUserEdit(newUserEdit);
      return;
    };

    loadUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  const chkAllPermissions = (checked: boolean) => {
    setUserEdit((prev) => {
      const next = { ...prev };

      permissions.forEach((perm) => {
        if (
          !INVERTED_ON_CHECK_ALL.includes(perm as any) &&
          !lockedPermissions.includes(perm)
        ) {
          (next as any)[perm] = checked;
        }
      });

      INVERTED_ON_CHECK_ALL.forEach((perm) => {
        if (!lockedPermissions.includes(perm)) {
          (next as any)[perm] = !checked;
        }
      });

      if (!checked && !lockedPermissions.includes("CheckTask")) {
        next.CheckTask = false;
      }

      return next;
    });
  };

  const roleChange = (role: UserRole | undefined) => {
    setUserEdit({ ...userEdit, role });
  };

  const canSubmit = () => {
    return !isLoadingUser() && !saving && requiredManager.validate();
  };

  const resendInvitation = async () => {
    const modalProps: DefaultModalProps = {
      message: TranslationService.translate.ResendInvitationConfirm,
      acceptButtonLabel: TranslationService.translate.Accept,
      onAcceptClick: async () => {
        const result = await UserService.reinvite(userEdit.email!);
        if (result instanceof Error) {
          showToast(
            TranslationService.translate.ErrorProcessingRequest,
            undefined,
            "danger"
          );
        } else {
          showToast(
            TranslationService.translate.ResendInvitationSent,
            undefined,
            "success"
          );
          ModalService.hideModal();
          //context.reload();
        }
      }
    };
    showDefaultModal(modalProps);
  };

  const submit = async () => {
    if (!canSubmit()) {
      return;
    }
    setSaving(true);
    if (!userEdit.chooseSegments) {
      userEdit.segments = [];
    }
    if (!userEdit.chooseNotificationSegments) {
      userEdit.notificationSegments = [];
    }
    const result = await UserService.set(userEdit);
    if (result instanceof Error) {
      const err = getError(result);
      let errorMessage = translate.ErrorProcessingRequest;
      if (err.status === 400) {
        if (err.message === "Fail-Exists") {
          errorMessage = translate.IdentifierAlreadyInUsed;
          showToast(errorMessage, undefined, "danger");
        }

        if (
          err.fullError.response &&
          err.fullError.response.data.message === "User-Limit"
        ) {
          var dataError = err.fullError.response.data;
          const modalProps: DefaultModalProps = {
            title: translate.LimitReached,
            message: () => (
              <>
                <UserLimit {...dataError} />{" "}
              </>
            ),
            acceptButtonLabel: TranslationService.translate.Accept,
            size: "modal-lg",
            hideClose: true,
            onAcceptClick: async () => {
              ModalService.hideModal();
            }
          };
          showDefaultModal(modalProps);
        }
      } else {
        showToast(errorMessage, undefined, "danger");
      }
      setSaving(false);
    } else {
      showToast(translate.SavedChanges, undefined, "success");
      onSubmit && onSubmit();
      FloatingPanelService.hidePanel();
    }
  };

  const cancelReturn = () => FloatingPanelService.hidePanel();

  useEffect(() => {
    const selectedRoles = roles?.filter((r) =>
      userEdit.roles?.includes(r.RoleID)
    );
    const newLocked: string[] = [];

    if (selectedRoles && selectedRoles.length > 0) {
      const updatedUser = { ...userEdit };

      permissions.forEach((permission) => {
        // Para permisos invertidos, la lógica es diferente
        if (INVERTED_ON_CHECK_ALL.includes(permission as any)) {
          // Para permisos invertidos: si el rol tiene el permiso en false, significa que el rol otorga ese permiso
          // Por ejemplo: DisableIOStatusEdit = false significa que SÍ puede cambiar estado
          const hasPermissionInAnyRole = selectedRoles.some(
            (r) => r[permission] === false
          );

          if (hasPermissionInAnyRole) {
            (updatedUser as any)[permission] = false;
            newLocked.push(permission);
          }
        } else {
          // Para permisos normales: si el rol tiene el permiso en true, significa que el rol otorga ese permiso
          const hasPermissionInAnyRole = selectedRoles.some(
            (r) => r[permission]
          );

          if (hasPermissionInAnyRole) {
            (updatedUser as any)[permission] = true;
            newLocked.push(permission);
          }
        }
      });

      // Permisos cashapp (solo visual): union de los Cashapp* de los roles seleccionados.
      // Los objetos de rol traen las columnas Cashapp* igual que las de cobranzas; esto mantiene
      // el tab Cashapp en vivo al cambiar los roles (el backend solo lo calcula al abrir).
      const newCashAppPermissions: { [key: string]: boolean } = {};
      cashappPermissionKeys.forEach((permission) => {
        newCashAppPermissions[permission] = selectedRoles.some(
          (r) => (r as any)[permission] === true
        );
      });
      updatedUser.cashAppPermissions = newCashAppPermissions;

      setLockedPermissions(newLocked);
      setUserEdit(updatedUser);
    } else {
      // Si no hay roles asignados, limpiar los permisos bloqueados y los permisos cashapp visuales.
      setLockedPermissions([]);
      setUserEdit((v) => ({ ...v, cashAppPermissions: {} }));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEdit.roles]);

  useEffect(() => {
    // Los roles son compartidos (cobranzas + cashapp). Solo se limpian al salir de Invitado
    // cuando la empresa NO tiene cashapp; si tiene cashapp hay que preservarlos porque tambien
    // otorgan permisos cashapp (un usuario puede ser Admin en cobranzas e Invitado en cashapp).
    if (userEdit.role !== UserRole.Guest && !CompanyService.canShowCashApp()) {
      const updatedUser = { ...userEdit };
      setLockedPermissions([]);
      setUserEdit({ ...updatedUser, roles: [] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userEdit.role]);

  // Cuando la empresa tiene Cashapp, el form se agrupa en tabs (Cobranzas / Cashapp).
  const showCashapp = CompanyService.canShowCashApp();
  // El tab Cashapp solo esta disponible si el usuario tiene el switch prendido.
  const cashappTabEnabled = showCashapp && userEdit.CashApp;
  const currentTab =
    activeTab === "cashapp" && cashappTabEnabled ? "cashapp" : "cobranzas";
  // Permisos cashapp del cobranzas tab (cobranzas) y del cashapp tab (visual).
  // Cuenta el estado MOSTRADO del checkbox (2 permisos invertidos en la UI),
  // para que el budget coincida con "Marcar todo".
  const isCobranzasPermissionChecked = (permission: string): boolean => {
    if (permission === "DisableIOStatusEdit") {
      return !userEdit.DisableIOStatusEdit;
    }
    if (permission === "CheckTask") {
      return userEdit.EditTask || userEdit.CheckTask;
    }
    if (permission === "CheckOthersTask") {
      return (
        userEdit.EditTask || (userEdit.CheckTask && !userEdit.CheckOthersTask)
      );
    }
    return userEdit[permission as keyof UserEditModel] as boolean;
  };
  const cobranzasPermissionCount = permissions.filter(
    isCobranzasPermissionChecked
  ).length;
  const cashappPermissionCount = cashappPermissionKeys.filter(
    (permission) => userEdit.cashAppPermissions[permission]
  ).length;

  const profileItems = [
    { value: UserRole.Admin, text: translate.AdminUser },
    { value: UserRole.Guest, text: translate.Guest },
    { value: UserRole.QueriesOnly, text: translate.QueryUser }
  ];
  const profileDropdown = (
    <Dropdown
      items={profileItems}
      onChange={(role: UserRole) => roleChange(role)}
      value={userEdit.role}
      readonly={readonly || editPanel}
    />
  );
  const cashappProfileDropdown = (
    <Dropdown
      items={profileItems}
      onChange={(role: UserRole) =>
        setUserEdit((v) => ({ ...v, cashAppRole: role }))
      }
      value={userEdit.cashAppRole}
      readonly={readonly}
    />
  );

  return (
    <>
      <div className="floatingBody p-4">
        {isLoadingUser() && (
          <i className="fas fa-spinner-third fa-spin third ms-2"></i>
        )}
        {!isLoadingUser() && canOnlyEditPanel() && (
          <>
            <div className="row ">
              <div className="col-6">
                <label className="form-label me-4 w-75px">
                  {translate.Panel}
                </label>
                <Dropdown
                  items={panelOptions}
                  onChange={updateUser("DashboardPanelID")}
                  optionLabel={translate.Standard}
                  value={userEdit.DashboardPanelID}
                  readonly={readonly}
                />
              </div>
            </div>

            {/* Bootstrap cashapp (Opcion 2): el Creador se auto-habilita cashapp aqui, sin exponer
                su perfil/permisos de cobranzas. Luego gestiona el cashapp del resto por el form normal. */}
            {ownerSelfEdit && showCashapp && (
              <>
                <div className="row mb-3 mt-3">
                  <div className="col-6">
                    <div className="d-flex input-column">
                      <label className="form-label me-4 w-75px">
                        {translate.Cashapp}
                      </label>
                      <SwitchButton
                        disabled={readonly}
                        onChange={updateUser("CashApp")}
                        defaultValue={userEdit.CashApp}
                      />
                    </div>
                  </div>
                </div>

                {cashappTabEnabled && (
                  <>
                    <div className="row mb-3">
                      <div className="col-6">
                        <div className="d-flex input-column">
                          <label className="form-label me-4 w-75px">
                            {translate.Profile}
                          </label>
                          {cashappProfileDropdown}
                        </div>
                      </div>
                    </div>

                    {userEdit.cashAppRole === UserRole.Guest && (
                      <>
                        <div className="row mb-4">
                          <div className="col-6">
                            <label className="form-label me-4 w-75px">
                              {translate.Roles}
                            </label>
                            <Select
                              isMulti
                              defaultValue={rolesSelected}
                              value={rolesSelected}
                              options={roles?.map((x) => ({
                                label: x.Name,
                                value: x.RoleID
                              }))}
                              onChange={(values) =>
                                setUserEdit((v) => ({
                                  ...v,
                                  roles: [...values.map((va) => va.value)]
                                }))
                              }
                              placeholder={translate.Select}
                              classNamePrefix={"multiple-choice-select"}
                              isDisabled={readonly}
                            />
                          </div>
                        </div>

                        <div className="row mb-2">
                          <div className="col-auto">
                            <h2 className="pe-4 pb-1">
                              {translate.Permissions}
                            </h2>
                          </div>
                        </div>

                        {cashappPermissionGroups.map((group) => (
                          <div className="row" key={group.titleKey}>
                            <div className="col">
                              <div className="d-flex input-column mb-3">
                                <div className="d-flex flex-wrap">
                                  <label className="form-label me-4 w-75px">
                                    {translate[group.titleKey]}
                                  </label>
                                  {group.items.map((item) => (
                                    <CheckBoxEditor
                                      key={item.key}
                                      disabled
                                      onChange={() => undefined}
                                      message={translate[item.labelKey]}
                                      value={
                                        userEdit.cashAppPermissions[item.key] ??
                                        false
                                      }
                                    ></CheckBoxEditor>
                                  ))}
                                </div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
        {!isLoadingUser() && !canOnlyEditPanel() && (
          <>
            <div className="row">
              <div className="col-6">
                <div className="d-flex input-column mb-3">
                  <label className="form-label me-4 w-75px">
                    {translate.Email}
                  </label>
                  {userEdit.UserID ? (
                    <div className="d-flex align-items-center">
                      <span>{userEdit.email}</span>
                      {!hasPassword && !readonly && (
                        <button
                          className="btn btn-link ms-3"
                          onClick={resendInvitation}
                        >
                          {translate.ResendInvitation}
                        </button>
                      )}
                    </div>
                  ) : (
                    <>
                      <TextEditor
                        onChange={onEmailChange}
                        defaultValue={userEdit.email}
                      />
                      <ValidationMessage
                        onChange={onEmailChange}
                        message={translate.RequiredField}
                        validationMethod={validateMail}
                        defaultValue={userEdit.email}
                      />
                    </>
                  )}
                </div>
              </div>
              <div className="col-6">
                {showCashapp ? (
                  <div className="d-flex input-column mb-3">
                    <label className="form-label me-4 w-75px">
                      {translate.Cashapp}
                    </label>
                    <SwitchButton
                      disabled={readonly}
                      onChange={updateUser("CashApp")}
                      defaultValue={userEdit.CashApp}
                    />
                  </div>
                ) : (
                  <div className="d-flex input-column mb-3">
                    <label className="form-label me-4 w-75px">
                      {translate.Profile}
                    </label>
                    {profileDropdown}
                  </div>
                )}
              </div>
            </div>

            {userEdit.role === UserRole.Guest || showCashapp ? (
              <div className="row mb-4">
                <div className="col-6">
                  <label className="form-label me-4 w-75px">
                    {translate.Roles}
                  </label>
                  <Select
                    isMulti
                    defaultValue={rolesSelected}
                    value={rolesSelected}
                    options={roles?.map((x) => ({
                      label: x.Name,
                      value: x.RoleID
                    }))}
                    onChange={(values) =>
                      setUserEdit((v) => ({
                        ...v,
                        roles: [...values.map((va) => va.value)]
                      }))
                    }
                    placeholder={translate.Select}
                    classNamePrefix={"multiple-choice-select"}
                    isDisabled={readonly}
                  />
                </div>
              </div>
            ) : null}

            {showCashapp && (
              <Tabs
                activeId={currentTab}
                onChange={(id) => setActiveTab(id as "cobranzas" | "cashapp")}
                items={[
                  {
                    id: "cobranzas",
                    label: translate.Cobranzas,
                    badge:
                      userEdit.role === UserRole.Guest
                        ? `${cobranzasPermissionCount}/${permissions.length}`
                        : undefined
                  },
                  ...(cashappTabEnabled
                    ? [
                        {
                          id: "cashapp",
                          label: translate.Cashapp,
                          badge:
                            userEdit.cashAppRole === UserRole.Guest
                              ? `${cashappPermissionCount}/${cashappPermissionKeys.length}`
                              : undefined
                        }
                      ]
                    : [])
                ]}
              />
            )}

            {(!showCashapp || currentTab === "cobranzas") && (
              <>
                {showCashapp && (
                  <div className="row mb-3">
                    <div className="col-6">
                      <div className="d-flex input-column">
                        <label className="form-label me-4 w-75px">
                          {translate.Profile}
                        </label>
                        {profileDropdown}
                      </div>
                    </div>
                  </div>
                )}

                {userEdit.role === UserRole.Guest && (
                  <>
                    <div className="row mb-2" style={{ alignItems: "end" }}>
                      <div className="col-auto">
                        <h2 className="pe-4 pb-1">{translate.Permissions}</h2>
                      </div>
                      <div className="col">
                        <CheckBoxEditor
                          disabled={readonly}
                          onChange={chkAllPermissions}
                          message={translate.CheckAll}
                        ></CheckBoxEditor>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.Clients}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("NewPerson")
                              }
                              onChange={updateUser("NewPerson")}
                              message={translate.Create}
                              value={userEdit.NewPerson}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("EditPerson")
                              }
                              onChange={updateUser("EditPerson")}
                              message={translate.Edit}
                              value={userEdit.EditPerson}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("DeletePerson")
                              }
                              onChange={updateUser("DeletePerson")}
                              message={translate.Delete}
                              value={userEdit.DeletePerson}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("SetFieldsPerson")
                              }
                              onChange={updateUser("SetFieldsPerson")}
                              message={translate.SetFields}
                              value={userEdit.SetFieldsPerson}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.Invoices}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly || lockedPermissions.includes("NewIO")
                              }
                              onChange={updateUser("NewIO")}
                              message={translate.Create}
                              value={userEdit.NewIO}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly || lockedPermissions.includes("EditIO")
                              }
                              onChange={updateUser("EditIO")}
                              message={translate.Edit}
                              value={userEdit.EditIO}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("DeleteIO")
                              }
                              onChange={updateUser("DeleteIO")}
                              message={translate.Delete}
                              value={userEdit.DeleteIO}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("SetFieldsIO")
                              }
                              onChange={updateUser("SetFieldsIO")}
                              message={translate.SetFields}
                              value={userEdit.SetFieldsIO}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.EstadosFacturas}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("EditIOStatus")
                              }
                              onChange={updateUser("EditIOStatus")}
                              message={translate.Configure}
                              value={userEdit.EditIOStatus}
                            ></CheckBoxEditor>
                            <div className="d-flex align-items-center">
                              <CheckBoxEditor
                                addClass="my-0"
                                disabled={
                                  readonly ||
                                  lockedPermissions.includes(
                                    "DisableIOStatusEdit"
                                  )
                                }
                                onChange={(checked) =>
                                  updateUser("DisableIOStatusEdit")(!checked)
                                }
                                message={translate.ChangeStatus}
                                value={!userEdit.DisableIOStatusEdit}
                              ></CheckBoxEditor>
                              <TooltipComponent
                                title={`Al marcar esta opción, el usuario podrá cambiar los estados de las facturas. Por ejemplo, podrá cambiar una factura de un estado “Normal” a “Promesa de pago`}
                              >
                                <i className="fal fa-question-circle ms-2" />
                              </TooltipComponent>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.Payments}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("NewTransaction")
                              }
                              onChange={updateUser("NewTransaction")}
                              message={translate.Create}
                              value={userEdit.NewTransaction}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("EditTransaction")
                              }
                              onChange={updateUser("EditTransaction")}
                              message={translate.Edit}
                              value={userEdit.EditTransaction}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("DeleteTransaction")
                              }
                              onChange={updateUser("DeleteTransaction")}
                              message={translate.Delete}
                              value={userEdit.DeleteTransaction}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes(
                                  "SetFieldsTransaction"
                                )
                              }
                              onChange={updateUser("SetFieldsTransaction")}
                              message={translate.SetFields}
                              value={userEdit.SetFieldsTransaction}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.PaymentTypes}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes(
                                  "EditTransactionType"
                                )
                              }
                              onChange={updateUser("EditTransactionType")}
                              message={translate.SetFields}
                              value={userEdit.EditTransactionType}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.Comments}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("NewComment")
                              }
                              onChange={updateUser("NewComment")}
                              message={translate.Create}
                              value={userEdit.NewComment}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("EditComment")
                              }
                              onChange={updateUser("EditComment")}
                              message={translate.Edit}
                              value={userEdit.EditComment}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("DeleteComment")
                              }
                              onChange={updateUser("DeleteComment")}
                              message={translate.Delete}
                              value={userEdit.DeleteComment}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("ImportComment")
                              }
                              onChange={updateUser("ImportComment")}
                              message={translate.ImportAction}
                              value={userEdit.ImportComment}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.Whatsapp}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("NewWhatsapp")
                              }
                              onChange={updateUser("NewWhatsapp")}
                              message={translate.Create}
                              value={userEdit.NewWhatsapp}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("EditWhatsapp")
                              }
                              onChange={updateUser("EditWhatsapp")}
                              message={translate.Edit}
                              value={userEdit.EditWhatsapp}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("DeleteWhatsapp")
                              }
                              onChange={updateUser("DeleteWhatsapp")}
                              message={translate.Delete}
                              value={userEdit.DeleteWhatsapp}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes(
                                  "SetTemplateWhatsapp"
                                )
                              }
                              onChange={updateUser("SetTemplateWhatsapp")}
                              message={translate.SetTemplate}
                              value={userEdit.SetTemplateWhatsapp}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.Emails}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("NewMail")
                              }
                              onChange={updateUser("NewMail")}
                              message={translate.Send}
                              value={userEdit.NewMail}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("EditMail")
                              }
                              onChange={updateUser("EditMail")}
                              message={translate.Edit}
                              value={userEdit.EditMail}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("DeleteMail")
                              }
                              onChange={updateUser("DeleteMail")}
                              message={translate.Delete}
                              value={userEdit.DeleteMail}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("SetTemplateEmail")
                              }
                              onChange={updateUser("SetTemplateEmail")}
                              message={translate.SetTemplate}
                              value={userEdit.SetTemplateEmail}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.SMSTemplates}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("SetTemplateSMS")
                              }
                              onChange={updateUser("SetTemplateSMS")}
                              message={translate.Configure}
                              value={userEdit.SetTemplateSMS}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.PaymentGateway}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("SetPayment")
                              }
                              onChange={updateUser("SetPayment")}
                              message={translate.Configure}
                              value={userEdit.SetPayment}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.Tasks}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("NewTask")
                              }
                              onChange={updateUser("NewTask")}
                              message={translate.Create}
                              value={userEdit.NewTask}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("EditTask")
                              }
                              onChange={(x) => {
                                updateUser("EditTask")(x);
                                updateUser("CheckTask")(false);
                                updateUser("CheckOthersTask")(true);
                              }}
                              message={translate.Edit}
                              value={userEdit.EditTask}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("DeleteTask")
                              }
                              onChange={updateUser("DeleteTask")}
                              message={translate.Delete}
                              value={userEdit.DeleteTask}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              onChange={(x) => {
                                updateUser("CheckTask")(x);
                                updateUser("CheckOthersTask")(true);
                              }}
                              message={translate.MarkAsCompleted}
                              value={userEdit.EditTask || userEdit.CheckTask}
                              disabled={readonly || userEdit.EditTask}
                              addClass="me-5 my-0 col-auto"
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              onChange={(checked) =>
                                updateUser("CheckOthersTask")(!checked)
                              }
                              message={translate.OnlyMarkOwnTasks}
                              value={
                                userEdit.EditTask ||
                                (userEdit.CheckTask &&
                                  !userEdit.CheckOthersTask)
                              }
                              disabled={
                                readonly ||
                                userEdit.EditTask ||
                                !userEdit.CheckTask
                              }
                              addClass="col-auto"
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.Tags}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly || lockedPermissions.includes("NewTag")
                              }
                              onChange={updateUser("NewTag")}
                              message={translate.Create}
                              value={userEdit.NewTag}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("EditTag")
                              }
                              onChange={updateUser("EditTag")}
                              message={translate.Edit}
                              value={userEdit.EditTag}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("DeleteTag")
                              }
                              onChange={updateUser("DeleteTag")}
                              message={translate.Delete}
                              value={userEdit.DeleteTag}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.Reports}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("ViewReport")
                              }
                              onChange={updateUser("ViewReport")}
                              message={translate.View}
                              value={userEdit.ViewReport}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly || lockedPermissions.includes("Export")
                              }
                              onChange={updateUser("Export")}
                              message={translate.ExportData}
                              value={userEdit.Export}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.Segments}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("EditSegment")
                              }
                              onChange={updateUser("EditSegment")}
                              message={translate.Configure}
                              value={userEdit.EditSegment}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.Automatic}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("ViewAction")
                              }
                              onChange={updateUser("ViewAction")}
                              message={translate.View}
                              value={userEdit.ViewAction}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("EditAction")
                              }
                              onChange={updateUser("EditAction")}
                              message={translate.Configure}
                              value={userEdit.EditAction}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.Company}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("SetCompany")
                              }
                              onChange={updateUser("SetCompany")}
                              message={translate.Configure}
                              value={userEdit.SetCompany}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.Roles}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("ViewRole")
                              }
                              onChange={updateUser("ViewRole")}
                              message={translate.View}
                              value={userEdit.ViewRole}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("EditRole")
                              }
                              onChange={updateUser("EditRole")}
                              message={translate.Configure}
                              value={userEdit.EditRole}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.Users}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("ViewUser")
                              }
                              onChange={updateUser("ViewUser")}
                              message={translate.View}
                              value={userEdit.ViewUser}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("EditUser")
                              }
                              onChange={updateUser("EditUser")}
                              message={translate.Configure}
                              value={userEdit.EditUser}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.ActivityType}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("EditActivityType")
                              }
                              onChange={updateUser("EditActivityType")}
                              message={translate.Configure}
                              value={userEdit.EditActivityType}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.Importaciones}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("ViewImportLog")
                              }
                              onChange={updateUser("ViewImportLog")}
                              message={translate.ViewLogs}
                              value={userEdit.ViewImportLog}
                            ></CheckBoxEditor>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("SetImport")
                              }
                              onChange={updateUser("SetImport")}
                              message={translate.Configure}
                              value={userEdit.SetImport}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.Dashboards}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("EditDashboard")
                              }
                              onChange={updateUser("EditDashboard")}
                              message={translate.Configure}
                              value={userEdit.EditDashboard}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.OtherSettings}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes(
                                  "SetCompanyOtherSettings"
                                )
                              }
                              onChange={updateUser("SetCompanyOtherSettings")}
                              message={translate.Configure}
                              value={userEdit.SetCompanyOtherSettings}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.AIAssistant}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly ||
                                lockedPermissions.includes("SetAIAssistant")
                              }
                              onChange={updateUser("SetAIAssistant")}
                              message={translate.Configure}
                              value={userEdit.SetAIAssistant}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex">
                            <label className="form-label me-4 w-75px">
                              {translate.MCP}
                            </label>
                            <CheckBoxEditor
                              disabled={
                                readonly || lockedPermissions.includes("UseMCP")
                              }
                              onChange={updateUser("UseMCP")}
                              message={translate.Use}
                              value={userEdit.UseMCP}
                            ></CheckBoxEditor>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
                {(userEdit.role === UserRole.Guest ||
                  userEdit.role === UserRole.QueriesOnly) && (
                  <>
                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex align-items-center gap-4">
                            <label className="form-label col-auto">
                              {translate.ClientAccess}
                            </label>

                            <div className="col">
                              <Dropdown
                                items={[
                                  {
                                    value: "false",
                                    text: translate.AllClients
                                  },
                                  {
                                    value: "true",
                                    text: translate.OnlyClientsInSelectedSegments
                                  }
                                ]}
                                onChange={(val) =>
                                  updateUser("chooseSegments")(val === "true")
                                }
                                value={userEdit.chooseSegments.toString()}
                                readonly={readonly}
                              />
                            </div>
                          </div>
                          {userEdit.chooseSegments && (
                            <MultiselectEditor
                              className="mt-4"
                              items={segmentOptionList
                                .map((x) => ({
                                  value: x.Id.toString(),
                                  label: x.Value
                                }))
                                .sort((a, b) => a.label.localeCompare(b.label))}
                              callback={updateUser("segments")}
                              placeholder={translate.SelectSegments}
                              value={selectedSegmentsValue}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}

                {(userEdit.role === UserRole.Guest ||
                  userEdit.role === UserRole.Admin) && (
                  <>
                    <div className="row">
                      <div className="col">
                        <div className="d-flex input-column mb-3">
                          <div className="d-flex align-items-center gap-4">
                            <label className="form-label col-auto">
                              {translate.NotificationsAccess}
                            </label>

                            <div className="col">
                              <Dropdown
                                items={[
                                  {
                                    value: "false",
                                    text: translate.AllClients
                                  },
                                  {
                                    value: "true",
                                    text: translate.OnlyClientsInSelectedSegments
                                  }
                                ]}
                                onChange={(val) =>
                                  updateUser("chooseNotificationSegments")(
                                    val === "true"
                                  )
                                }
                                value={userEdit.chooseNotificationSegments.toString()}
                                readonly={readonly}
                              />
                            </div>
                          </div>
                          {userEdit.chooseNotificationSegments && (
                            <MultiselectEditor
                              className="mt-4"
                              items={segmentOptionList
                                .map((x) => ({
                                  value: x.Id.toString(),
                                  label: x.Value
                                }))
                                .sort((a, b) => a.label.localeCompare(b.label))}
                              callback={updateUser("notificationSegments")}
                              placeholder={translate.SelectSegments}
                              value={selectedNotificationSegmentsValue}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </>
                )}
                <hr />
                <div className="row">
                  <div className="col">
                    <div className="d-flex input-column mb-3">
                      <div className="d-flex align-items-center gap-4">
                        <label className="form-label col-auto">
                          {translate.Dashboards}
                        </label>
                        <div className="col">
                          <Dropdown
                            items={panelOptions}
                            onChange={updateUser("DashboardPanelID")}
                            optionLabel={translate.Standard}
                            value={userEdit.DashboardPanelID}
                            readonly={readonly}
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}

            {cashappTabEnabled && currentTab === "cashapp" && (
              <>
                <div className="row mb-3">
                  <div className="col-6">
                    <div className="d-flex input-column">
                      <label className="form-label me-4 w-75px">
                        {translate.Profile}
                      </label>
                      {cashappProfileDropdown}
                    </div>
                  </div>
                </div>

                {userEdit.cashAppRole === UserRole.Guest && (
                  <>
                    <div className="row mb-2">
                      <div className="col-auto">
                        <h2 className="pe-4 pb-1">{translate.Permissions}</h2>
                      </div>
                    </div>

                    {cashappPermissionGroups.map((group) => (
                      <div className="row" key={group.titleKey}>
                        <div className="col">
                          <div className="d-flex input-column mb-3">
                            <div className="d-flex flex-wrap">
                              <label className="form-label me-4 w-75px">
                                {translate[group.titleKey]}
                              </label>
                              {group.items.map((item) => (
                                <CheckBoxEditor
                                  key={item.key}
                                  disabled
                                  onChange={() => undefined}
                                  message={translate[item.labelKey]}
                                  value={
                                    userEdit.cashAppPermissions[item.key] ??
                                    false
                                  }
                                ></CheckBoxEditor>
                              ))}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </>
        )}
      </div>
      <FloatingPanelFooter>
        {readonly ? (
          <button className="btn btn-primary me-3" onClick={cancelReturn}>
            {translate.Cancel}
          </button>
        ) : (
          <>
            <button className="btn btn-primary me-3" onClick={submit}>
              {userId ? translate.Save : translate.Invite}
              {saving && (
                <i className="fas fa-spinner-third fa-spin third ms-2"></i>
              )}
            </button>
          </>
        )}
      </FloatingPanelFooter>
    </>
  );
};
